import { safeJson } from "src/app/helpers/Index";
import { faIdCard, faTicket, faTrash, faTrophy } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Dialog } from "primereact/dialog";
import { InputNumber } from "primereact/inputnumber";
import { InputSwitch } from "primereact/inputswitch";
import { InputText } from "primereact/inputtext";
import { MultiSelect } from "primereact/multiselect";
import { Toast } from "primereact/toast";
import React from "react";
import { CreateFixture } from "src/app/components/CreateFixture";
import { ScheduleHero, ScheduleHeroEmpty } from "src/app/components/ScheduleHero";
import { AdminFixtures } from "src/app/pages/admin/fixtures/AdminFixtures";
import { FixtureTicketingDetail } from "src/app/components/FixtureTicketingDetail";
import { SaleWindows } from "src/app/components/SaleWindows";
import { ClubContext, HasFeature } from "src/app/helpers/ClubContext";
import { CompetitionService, FixtureService, SaleWindowParent, SeasonTicketService } from "src/app/services/Index";
import { TicketingService } from "src/app/services/TicketingService";
import "./AdminTicketing.css";

/** Which ticketing page this is. Each has its own menu entry and route. */
export type TicketingSection = "types" | "fixtures" | "season" | "competitions";

type Props = { section?: TicketingSection };
type State = {
	available: boolean;
	types: any[];
	fixtures: any[];
	selectedFixtureId: number | null;
	fixtureConfig: any;
	showTypeDialog: boolean;
	editType: any;
	isLoading: boolean;
	saving: boolean;
	// Season tickets
	seasonAvailable: boolean;
	competitions: any[];
	seasonProducts: any[];
	holders: any[];
	holdersProductId: number | null;
	showProductDialog: boolean;
	editProduct: any;
	showIssueDialog: boolean;
	issueForm: any;
	showCompetitionDialog: boolean;
	windowsForProductId: number | null;
	showCreateFixture: boolean;
	fixtureView: "calendar" | "list";
	showFixtureDialog: boolean;
	summaries: { [fixtureId: number]: any };
	calendarMonth: Date;
	editFixture: any;
	club: any;
	editCompetition: any;
};

/** Pounds from pence, for display. */
const toPounds = (pence: number) => ((Number(pence) || 0) / 100);

const money = (pence: any) =>
	`£${toPounds(pence).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * The season a club is in on a given date, using the 31 July convention.
 * Only a default for the date pickers — products store explicit dates so a
 * mid-season product can be sold pro-rata without special-casing.
 */
const defaultSeason = () => {
	const now = new Date();
	const startYear = now.getMonth() >= 6 ? now.getFullYear() : now.getFullYear() - 1;
	return { end: new Date(startYear + 1, 6, 31), start: new Date(startYear, 6, 31) };
};

const seasonLabel = (from: any, to: any) => {
	const a = new Date(from), b = new Date(to);
	if (isNaN(a.getTime()) || isNaN(b.getTime())) return "";
	return `${a.toLocaleDateString("en-GB")} – ${b.toLocaleDateString("en-GB")}`;
};

const SECTION_TITLES: { [key: string]: string } = {
	competitions: "Competitions",
	fixtures: "Fixture Ticketing",
	season: "Season Tickets",
	types: "Ticket Types"
};

const STATUS_LABELS: { [key: number]: string } = { 0: "Active", 10: "Suspended", 20: "Void" };

/**
 * Ticketing setup: the club's ticket types, and which fixtures are on sale.
 *
 * Ticketing is opt-in — a club that hasn't configured anything sees an empty
 * state here and no change anywhere else in the product. If the API reports
 * the feature unavailable (schema not migrated), the page says so plainly.
 */
export class AdminTicketing extends React.Component<Props, State> {
	toast: any;

	constructor(props: Props) {
		super(props);
		this.state = {
			available: true, types: [], fixtures: [],
			selectedFixtureId: null, fixtureConfig: null, showTypeDialog: false,
			editType: null, isLoading: true, saving: false,
			seasonAvailable: true, competitions: [], seasonProducts: [], holders: [],
			holdersProductId: null, showProductDialog: false, editProduct: null,
			showIssueDialog: false, issueForm: null, showCompetitionDialog: false,
			editCompetition: null, windowsForProductId: null, club: null,
			showCreateFixture: false, editFixture: null, showFixtureDialog: false, summaries: {},
			fixtureView: "list", calendarMonth: new Date()
		};
	}

	componentDidMount() {
		this.load();
		this.loadSeason();
		this.setState({ club: ClubContext.club });
	}

	async load() {
		// Only the status call decides whether ticketing is switched on. A
		// fixtures or types call that fails is a broken request, not an
		// uninstalled feature, and telling a club it hasn't got ticketing
		// because one endpoint hiccuped sends them looking for the wrong
		// problem entirely.
		try {
			const status = await safeJson(TicketingService.status());
			if (status && status.available === false) {
				this.setState({ available: false, isLoading: false });
				return;
			}
		} catch {
			this.setState({ available: false, isLoading: false });
			return;
		}

		const types = await this.readArray(() => TicketingService.getTypes());

		// Prefer the endpoint that carries team names; fall back to the plain
		// one so the list still works if it isn't there.
		this.loadSummaries();

		let fixtures = await this.readArray(() => FixtureService.getFixturesWithTeams());
		if (!fixtures.length) fixtures = await this.readArray(() => FixtureService.getAll());

		// Future fixtures first — those are the ones you put on sale.
		const upcoming = fixtures
			.filter((f: any) => new Date(f.fixtureDateTime) >= new Date(Date.now() - 86400000))
			.sort((a: any, b: any) => new Date(a.fixtureDateTime).getTime() - new Date(b.fixtureDateTime).getTime());

		this.setState({ types, fixtures: upcoming, isLoading: false });
	}

	/** A list from an endpoint, or an empty one — 204s and errors included. */
	async readArray(fn: () => Promise<Response>): Promise<any[]> {
		try {
			const res = await fn();
			if (!res.ok || res.status === 204) return [];
			const body = await res.json();
			return Array.isArray(body) ? body : [];
		} catch {
			return [];
		}
	}

	// ---------- season tickets ----------

	async loadSeason() {
		try {
			const status = await safeJson(SeasonTicketService.status());
			if (status && status.available === false) {
				this.setState({ seasonAvailable: false });
				return;
			}
			const [competitions, seasonProducts] = await Promise.all([
				(await CompetitionService.getAll()).json(),
				(await SeasonTicketService.getProducts()).json()
			]);
			this.setState({
				competitions: Array.isArray(competitions) ? competitions : [],
				seasonProducts: Array.isArray(seasonProducts) ? seasonProducts : []
			});
			this.loadHolders(this.state.holdersProductId ?? undefined);
		} catch {
			this.setState({ seasonAvailable: false });
		}
	}

	async loadHolders(productId?: number) {
		try {
			const holders = await safeJson(SeasonTicketService.getHolders(productId));
			this.setState({ holders: Array.isArray(holders) ? holders : [] });
		} catch {
			this.setState({ holders: [] });
		}
	}

	openProduct(product?: any) {
		const season = defaultSeason();
		this.setState({
			showProductDialog: true,
			editProduct: product
				? {
					...product,
					competitionIds: [...(product.competitionIds || [])],
					pricePounds: toPounds(product.pricePence),
					seasonEnd: new Date(product.seasonEnd),
					seasonStart: new Date(product.seasonStart)
				}
				: {
					capacity: null, competitionIds: [], isConcession: false, name: "",
					onSale: true, pricePounds: 0, seasonEnd: season.end, seasonStart: season.start,
					sortOrder: this.state.seasonProducts.length
				}
		});
	}

	async saveProduct() {
		const p = this.state.editProduct;
		if (!p?.name?.trim()) return;
		if (new Date(p.seasonEnd) <= new Date(p.seasonStart)) {
			this.toast.show({ closable: false, detail: "The season has to end after it starts.", life: 4000, severity: "warn", summary: "Check the dates" });
			return;
		}
		this.setState({ saving: true });
		const payload = {
			capacity: p.capacity || null,
			isConcession: !!p.isConcession,
			name: p.name.trim(),
			onSale: !!p.onSale,
			pricePence: Math.round((Number(p.pricePounds) || 0) * 100),
			seasonEnd: p.seasonEnd,
			seasonStart: p.seasonStart,
			sortOrder: Number(p.sortOrder) || 0
		};

		const res = p.id
			? await SeasonTicketService.updateProduct(p.id, payload)
			: await SeasonTicketService.addProduct(payload);

		if (!res.ok) {
			this.setState({ saving: false });
			this.toast.show({ closable: false, detail: "Please try again later.", life: 4000, severity: "error", summary: "Couldn't save" });
			return;
		}

		// Coverage is a separate call: the product has to exist before it can cover anything.
		const productId = p.id || (await res.json())?.id;
		if (productId) await SeasonTicketService.setCoverage(productId, p.competitionIds || []);

		this.setState({ editProduct: null, saving: false, showProductDialog: false });
		this.loadSeason();
	}

	async removeProduct(product: any) {
		if (!window.confirm(`Remove "${product.name}"? Season tickets already issued against it stay valid — this only stops it being sold.`)) return;
		await SeasonTicketService.removeProduct(product.id);
		this.loadSeason();
	}

	openIssue(productId?: number) {
		this.setState({
			issueForm: { holderEmail: "", holderName: "", seasonTicketProductId: productId ?? this.state.seasonProducts[0]?.id ?? null },
			showIssueDialog: true
		});
	}

	async issue() {
		const form = this.state.issueForm;
		if (!form?.seasonTicketProductId) return;
		this.setState({ saving: true });
		const res = await SeasonTicketService.issue(form);
		this.setState({ saving: false });
		if (!res.ok) {
			const message = await res.text();
			this.toast.show({ closable: false, detail: message || "Please try again later.", life: 5000, severity: "error", summary: "Couldn't issue" });
			return;
		}
		this.setState({ issueForm: null, showIssueDialog: false });
		this.toast.show({ closable: false, detail: "The pass is on the holder list.", life: 3000, severity: "success", summary: "Season ticket issued" });
		this.loadSeason();
	}

	async setHolderStatus(holder: any, status: number) {
		await SeasonTicketService.setStatus(holder.id, status);
		this.loadHolders(this.state.holdersProductId ?? undefined);
	}

	async exportHolders() {
		const res = await SeasonTicketService.exportHolders(this.state.holdersProductId ?? undefined);
		if (!res.ok) {
			this.toast.show({ closable: false, detail: "Please try again later.", life: 4000, severity: "error", summary: "Couldn't export" });
			return;
		}
		const blob = await res.blob();
		const url = window.URL.createObjectURL(blob);
		const link = document.createElement("a");
		link.download = "season-ticket-holders.csv";
		link.href = url;
		link.click();
		window.URL.revokeObjectURL(url);
	}

	// ---------- competitions ----------

	openCompetition(competition?: any) {
		this.setState({
			editCompetition: competition
				? { ...competition }
				: { name: "", shortName: "", sortOrder: this.state.competitions.length },
			showCompetitionDialog: true
		});
	}

	async saveCompetition() {
		const c = this.state.editCompetition;
		if (!c?.name?.trim()) return;
		this.setState({ saving: true });
		const payload = { name: c.name.trim(), shortName: (c.shortName || "").trim(), sortOrder: Number(c.sortOrder) || 0 };
		const res = c.id
			? await CompetitionService.updateSingleById(c.id, payload as any)
			: await CompetitionService.createSingle(payload as any);
		this.setState({ saving: false });
		if (!res.ok) {
			this.toast.show({ closable: false, detail: "Please try again later.", life: 4000, severity: "error", summary: "Couldn't save" });
			return;
		}
		this.setState({ editCompetition: null, showCompetitionDialog: false });
		this.loadSeason();
	}

	async removeCompetition(competition: any) {
		if (!window.confirm(`Remove "${competition.name}"? Fixtures already in it keep working, but no season ticket will cover it.`)) return;
		await CompetitionService.deleteSingleById(competition.id);
		this.loadSeason();
	}

	// ---------- ticket types ----------

	openType(type?: any) {
		this.setState({
			showTypeDialog: true,
			editType: type
				? { ...type, pricePounds: toPounds(type.pricePence) }
				: { name: "", pricePounds: 0, capacity: null, isConcession: false, sortOrder: this.state.types.length }
		});
	}

	async saveType() {
		const t = this.state.editType;
		if (!t?.name?.trim()) return;
		this.setState({ saving: true });
		const payload = {
			name: t.name.trim(),
			pricePence: Math.round((Number(t.pricePounds) || 0) * 100),
			capacity: t.capacity || null,
			isConcession: !!t.isConcession,
			sortOrder: Number(t.sortOrder) || 0
		};
		const res = t.id
			? await TicketingService.updateType(t.id, payload)
			: await TicketingService.addType(payload);
		this.setState({ saving: false });
		if (!res.ok) {
			this.toast.show({ closable: false, detail: "Please try again later.", life: 4000, severity: "error", summary: "Couldn't save" });
			return;
		}
		this.setState({ showTypeDialog: false, editType: null });
		this.load();
	}

	async removeType(type: any) {
		if (!window.confirm(`Remove "${type.name}"? Existing orders keep it — it just stops being offered.`)) return;
		await TicketingService.removeType(type.id);
		this.load();
	}

	// ---------- per-fixture ----------

	async selectFixture(fixtureId: number) {
		this.setState({ selectedFixtureId: fixtureId, fixtureConfig: null });
		try {
			const config = await safeJson(TicketingService.getFixture(fixtureId));
			this.setState({ fixtureConfig: config });
		} catch { /* leave it null; the panel shows a loading state */ }
	}

	async saveFixtureConfig(patch: any) {
		const { selectedFixtureId, fixtureConfig } = this.state;
		if (!selectedFixtureId) return;
		const next = { ...fixtureConfig, ...patch };
		this.setState({ fixtureConfig: next });
		await TicketingService.saveFixture(selectedFixtureId, {
			enabled: !!next.enabled,
			capacity: next.capacity || null,
			onSaleFrom: next.onSaleFrom || null,
			offSaleAt: next.offSaleAt || null,
			notes: next.notes || null
		});
	}

	async setPrice(ticketTypeId: number, pounds: number | null) {
		const { selectedFixtureId } = this.state;
		if (!selectedFixtureId) return;
		await TicketingService.setPrice(selectedFixtureId, ticketTypeId,
			pounds == null ? { pricePence: -1 } : { pricePence: Math.round(pounds * 100) });
		this.selectFixture(selectedFixtureId);
	}



	/** The fixture panel — side by side with the list, or over the calendar. */
	renderFixtureDetail() {
		const { selectedFixtureId, fixtureConfig, fixtures } = this.state;
		if (!selectedFixtureId) return null;
		if (!fixtureConfig && HasFeature("Ticketing")) {
			return <span style={{ color: "var(--pb-text-muted)" }}>Loading&hellip;</span>;
		}
		return (
			<FixtureTicketingDetail
				matchdaySlot={HasFeature("Scoreboard", true)
					? <AdminFixtures embedded fixtureId={selectedFixtureId} key={`md-${selectedFixtureId}`} />
					: undefined}
				ticketingEnabled={HasFeature("Ticketing")}
				onEdit={() => this.setState({
					editFixture: fixtures.find((f: any) => f.id === selectedFixtureId) || null,
					showCreateFixture: true
				})}
				club={this.state.club}
				config={fixtureConfig}
				fixture={fixtures.find((f: any) => f.id === selectedFixtureId)}
				onConfigChange={(patch) => this.saveFixtureConfig(patch)}
				onError={(message) => this.toast.show({ closable: false, detail: message, life: 5000, severity: "error", summary: "Ticketing" })}
				onInfo={(message) => this.toast.show({ closable: false, detail: message, life: 2500, severity: "success", summary: "Done" })}
				onSetPrice={(ticketTypeId, pounds) => this.setPrice(ticketTypeId, pounds)}
			/>
		);
	}

	/** Open a fixture from the calendar, where there's no room for a panel. */
	openFixtureDialog(id: number) {
		this.setState({ showFixtureDialog: true });
		this.selectFixture(id);
	}

	fixtureDialogTitle() {
		const fixture = this.state.fixtures.find((f: any) => f.id === this.state.selectedFixtureId);
		return fixture ? this.fixtureLabel(fixture) : "Fixture";
	}


	/** Sales figures for every fixture, keyed by id. */
	async loadSummaries() {
		try {
			const res = await TicketingService.getFixtureSummaries();
			if (!res.ok || res.status === 204) return;
			const body = await res.json();
			if (!body?.available || !Array.isArray(body.fixtures)) return;
			const summaries: any = {};
			for (const row of body.fixtures) summaries[row.fixtureId] = row;
			this.setState({ summaries });
		} catch {
			// No sales columns rather than no list.
		}
	}

	/** The fixture just gone and the one coming up. */
	heroFixtures() {
		const all = [...(this.state.fixtures || [])].sort(
			(a: any, b: any) => new Date(a.fixtureDateTime).getTime() - new Date(b.fixtureDateTime).getTime());
		const now = Date.now();
		const past = all.filter((f: any) => new Date(f.fixtureDateTime).getTime() < now);
		const future = all.filter((f: any) => new Date(f.fixtureDateTime).getTime() >= now);
		return { previous: past[past.length - 1] || null, next: future[0] || null };
	}

	fixtureStrip(fixture: any) {
		const when = new Date(fixture.fixtureDateTime);
		return [
			when.toLocaleDateString("en-GB", { day: "numeric", month: "short", weekday: "short", year: "2-digit" }),
			when.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }),
			fixture.venueName,
			fixture.competitionName
		].filter(Boolean).join(" | ");
	}

	/**
	 * Whether a fan can buy right now, at a glance.
	 *
	 * A dot reads down a column far faster than words do — you're scanning for
	 * the one fixture that isn't on sale, not reading each row.
	 */
	saleBadge(status: string | undefined) {
		const known: { [k: string]: { tone: string; label: string } } = {
			onsale: { tone: "green", label: "On sale" },
			scheduled: { tone: "amber", label: "Priority sales" }
		};
		const it = known[status || ""] || { tone: "red", label: "Not on sale" };
		// Leading the row, so the dot carries it alone — the label rides on the
		// tooltip and the aria-label rather than taking a column of width.
		return <span aria-label={it.label} className={`tk-dot ${it.tone}`} role="img" title={it.label}><i /></span>;
	}


	renderFixtureHeroes() {
		const { previous, next } = this.heroFixtures();
		const club = this.state.club;
		const side = (f: any) => ({ name: f.teamName || "TBC", badgeUrl: f.teamBadgeUrl });
		const us = { name: club?.name || "Home", badgeUrl: club?.badgeUrl };

		return (
			<div className="sh-row">
				{previous
					? <ScheduleHero away={side(previous)} home={us} label="Previous Fixture" onView={() => this.openFixtureDialog(previous.id)} strip={this.fixtureStrip(previous)} />
					: <ScheduleHeroEmpty label="Previous Fixture" message="No fixtures have been played yet." />}
				{next
					? <ScheduleHero away={side(next)} home={us} label="Next Fixture" onView={() => this.openFixtureDialog(next.id)} strip={this.fixtureStrip(next)} />
					: <ScheduleHeroEmpty label="Next Fixture" message="Nothing scheduled." />}
			</div>
		);
	}

	calendarNavigate(delta: number) {
		const m = this.state.calendarMonth;
		this.setState({ calendarMonth: new Date(m.getFullYear(), m.getMonth() + delta, 1) });
	}

	/**
	 * A month at a glance.
	 *
	 * Clubs think about a season as a calendar, not a list — "what have we got
	 * on over Easter" is the question this answers and a list can't.
	 */
	renderFixtureCalendar() {
		const { calendarMonth, fixtures, selectedFixtureId } = this.state;
		const monthStart = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), 1);
		const offset = (monthStart.getDay() + 6) % 7; // Monday-first grid
		const cells = Array.from({ length: 42 }, (_, i) => new Date(monthStart.getFullYear(), monthStart.getMonth(), 1 - offset + i));
		const today = new Date().toDateString();

		return (
			<div className="tk-calendar">
				<div className="tk-cal-header">
					<Button className="p-button-text p-button-sm" icon="pi pi-chevron-left" onClick={() => this.calendarNavigate(-1)} />
					<h5>{monthStart.toLocaleDateString("en-GB", { month: "long", year: "numeric" })}</h5>
					<Button className="p-button-text p-button-sm" icon="pi pi-chevron-right" onClick={() => this.calendarNavigate(1)} />
					<Button className="p-button-text p-button-sm" label="Today" onClick={() => this.setState({ calendarMonth: new Date() })} />
				</div>
				<div className="tk-cal-grid">
					{["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"].map((d) => <div className="tk-cal-dow" key={`dow-${d}`}>{d}</div>)}
					{cells.map((day, i) => {
						const onThisDay = fixtures.filter((f: any) => new Date(f.fixtureDateTime).toDateString() === day.toDateString());
						return (
							<div className={`tk-cal-cell${day.getMonth() !== monthStart.getMonth() ? " outside" : ""}${day.toDateString() === today ? " today" : ""}`} key={`cell-${i}`}>
								<div className="tk-cal-day">{day.getDate()}</div>
								{onThisDay.map((f: any) => (
									<button
										className={`tk-cal-fixture${selectedFixtureId === f.id ? " active" : ""}`}
										key={`cal-fx-${f.id}`}
										onClick={() => this.openFixtureDialog(f.id)}
										title={`${f.teamName || f.team?.teamName || "Fixture"} — ${new Date(f.fixtureDateTime).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })} KO`}
										type="button"
									>
										{new Date(f.fixtureDateTime).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}{" "}
										{f.teamName || f.team?.teamName || "Fixture"}
									</button>
								))}
							</div>
						);
					})}
				</div>
			</div>
		);
	}

	fixtureLabel(fixture: any) {
		const when = new Date(fixture.fixtureDateTime).toLocaleDateString("en-GB", { day: "numeric", month: "short", weekday: "short" });
		return `${when} — ${fixture.teamName || fixture.team?.teamName || "TBC"}`;
	}

	render() {
		const activeTab: TicketingSection = this.props.section || "types";
		const { available, types, fixtures, selectedFixtureId, fixtureConfig, isLoading } = this.state;
		const { competitions, holders, holdersProductId, seasonAvailable, seasonProducts } = this.state;
		const competitionName = (id: number) => competitions.find((c) => c.id === id)?.name || "Unknown";

		// Season tickets and competitions have their own tables, so a missing
		// ticketing schema shouldn't blank them out.
		const needsTicketingTables = activeTab === "types";
		if (!isLoading && !available && needsTicketingTables) {
			return (
				<div style={{ padding: 20 }}>
					<h2 style={{ marginBottom: 20 }}>{SECTION_TITLES[this.props.section || "types"]}</h2>
					<div className="tk-empty">
						<FontAwesomeIcon icon={faTicket} />
						<h3>Ticketing isn&rsquo;t switched on yet</h3>
						<p>The ticketing tables haven&rsquo;t been created on this environment. Once they are, this page will let you set up ticket types and put fixtures on sale.</p>
					</div>
				</div>
			);
		}

		return (
			<div style={{ padding: 20 }}>
				<Toast ref={(el) => (this.toast = el)} />
				<h2 style={{ marginBottom: 20 }}>{SECTION_TITLES[activeTab]}</h2>

				{activeTab === "types" && <>
					<div style={{ alignItems: "center", display: "flex", justifyContent: "space-between", marginBottom: 18 }}>
						<span style={{ color: "var(--pb-text-muted)" }}>The kinds of ticket you sell. Fixtures reuse these and can override the price.</span>
						<Button icon="pi pi-plus" label="Add type" onClick={() => this.openType()} />
					</div>

					{isLoading && <div style={{ color: "var(--pb-text-muted)" }}>Loading&hellip;</div>}

					{!isLoading && types.length === 0 &&
						<div className="tk-empty">
							<FontAwesomeIcon icon={faTicket} />
							<h3>No ticket types yet</h3>
							<p>Add Adult, Concession, Under 16 and so on. Nothing goes on sale until you enable a fixture.</p>
						</div>}

					<div className="tk-grid">
						{types.map((type) => (
							<div className="tk-tile" key={`tt-${type.id}`}>
								<div className="tk-tile-head">
									<FontAwesomeIcon icon={faTicket} />
									{type.isConcession && <span className="tk-badge">CONCESSION</span>}
								</div>
								<h4>{type.name}</h4>
								<span className="tk-price">£{toPounds(type.pricePence).toFixed(2)}</span>
								<span className="tk-meta">{type.capacity ? `Capacity ${type.capacity}` : "Uses fixture capacity"}</span>
								<div className="tk-tile-actions">
									<Button className="p-button-text" label="Edit" onClick={() => this.openType(type)} style={{ padding: "4px 8px" }} />
									<Button className="p-button-text p-button-danger" icon={<FontAwesomeIcon icon={faTrash} />} onClick={() => this.removeType(type)} style={{ padding: "4px 8px" }} />
								</div>
							</div>
						))}
					</div>
				</>}

				{activeTab === "fixtures" && this.renderFixtureHeroes()}

				{activeTab === "fixtures" && <div className="tk-split">
					<div className="tk-fixture-list full">
						<div className="tk-fixture-toolbar">
							<Button className="p-button-sm" icon="pi pi-plus" label="Add fixture" onClick={() => this.setState({ editFixture: null, showCreateFixture: true })} />
							<div className="tk-viewtoggle">
								<button className={this.state.fixtureView === "calendar" ? "active" : ""} onClick={() => this.setState({ fixtureView: "calendar" })} type="button">
									<i className="pi pi-calendar" /> Calendar
								</button>
								<button className={this.state.fixtureView === "list" ? "active" : ""} onClick={() => this.setState({ fixtureView: "list" })} type="button">
									<i className="pi pi-list" /> List
								</button>
							</div>
						</div>

						{this.state.fixtureView === "calendar" && this.renderFixtureCalendar()}

						{this.state.fixtureView === "list" && <>
							{fixtures.length === 0 && <span style={{ color: "var(--pb-text-muted)" }}>No upcoming fixtures.</span>}
							{fixtures.length > 0 && <div className="tk-table-wrap">
								<table className="tk-table">
									<thead>
										<tr>
											<th className="dot"></th>
											<th>Team</th>
											<th>Opposition</th>
											<th>Competition</th>
											<th>Location</th>
											<th>Venue</th>
											<th>Date</th>
											<th>Time</th>
											<th className="num">General</th>
											<th className="num">Hospitality</th>
											<th className="num">Total revenue</th>
											<th></th>
										</tr>
									</thead>
									<tbody>
										{fixtures.map((fixture: any) => {
											const when = new Date(fixture.fixtureDateTime);
											const sum = this.state.summaries[fixture.id];
											const home = fixture.location === 10;
											return (
												<tr className={selectedFixtureId === fixture.id ? "active" : ""} key={`fx-${fixture.id}`}>
													<td className="dot">{this.saleBadge(sum?.saleStatus)}</td>
													<td>
														<div className="tk-side">
															{this.state.club?.badgeUrl && <img alt="" src={this.state.club.badgeUrl} />}
															<span>{this.state.club?.name || "Home"}</span>
														</div>
													</td>
													<td>
														<div className="tk-side">
															{fixture.teamBadgeUrl && <img alt="" src={fixture.teamBadgeUrl} />}
															<span>{fixture.teamName || "TBC"}</span>
														</div>
													</td>
													<td>{fixture.competitionName || "\u2014"}</td>
													<td>{home ? "Away" : fixture.location === 20 ? "Neutral" : "Home"}</td>
													<td>{fixture.venueName || "\u2014"}</td>
													<td>{when.toLocaleDateString("en-GB")}</td>
													<td>{when.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}</td>
													<td className="num">{sum?.generalTickets ?? 0}</td>
													<td className="num">{sum?.hospitalityTickets ?? 0}</td>
													<td className="num">{money(sum?.revenuePence ?? 0)}</td>
													<td><Button className="p-button-sm" label="View" onClick={() => this.openFixtureDialog(fixture.id)} /></td>
												</tr>
											);
										})}
									</tbody>
								</table>
							</div>}
						</>}
					</div>

				</div>}

				{activeTab === "season" && <>
					{!seasonAvailable && <div className="tk-empty">
						<FontAwesomeIcon icon={faIdCard} />
						<h3>Season tickets aren&rsquo;t switched on yet</h3>
						<p>The season ticket tables haven&rsquo;t been created on this environment. Once they are, this tab will let you sell season tickets and manage holders.</p>
					</div>}

					{seasonAvailable && <>
						<div style={{ alignItems: "center", display: "flex", justifyContent: "space-between", marginBottom: 18 }}>
							<span style={{ color: "var(--pb-text-muted)" }}>What you sell for the whole season, and which competitions each one covers.</span>
							<Button icon="pi pi-plus" label="Add season ticket" onClick={() => this.openProduct()} />
						</div>

						{seasonProducts.length === 0 &&
							<div className="tk-empty">
								<FontAwesomeIcon icon={faIdCard} />
								<h3>No season tickets yet</h3>
								<p>Add one, choose the competitions it covers, and it can be issued to holders. Set up your competitions first if you haven&rsquo;t.</p>
							</div>}

						<div className="tk-grid">
							{seasonProducts.map((product) => (
								<div className="tk-tile" key={`st-${product.id}`}>
									<div className="tk-tile-head">
										<FontAwesomeIcon icon={faIdCard} />
										{product.isConcession && <span className="tk-badge">CONCESSION</span>}
										{!product.onSale && <span className="tk-badge tk-badge-off">OFF SALE</span>}
									</div>
									<h4>{product.name}</h4>
									<span className="tk-price">£{toPounds(product.pricePence).toFixed(2)}</span>
									<span className="tk-meta">{seasonLabel(product.seasonStart, product.seasonEnd)}</span>
									<span className="tk-meta">
										{product.issued} issued{product.capacity ? ` of ${product.capacity}` : ""}
									</span>
									<div className="tk-covers">
										{(product.competitionIds || []).length === 0
											? <span className="tk-warn">Covers nothing — pick competitions</span>
											: (product.competitionIds || []).map((id: number) => (
												<span className="tk-chip" key={`cv-${product.id}-${id}`}>{competitionName(id)}</span>
											))}
									</div>
									<div className="tk-tile-actions">
										<Button className="p-button-text" label="Edit" onClick={() => this.openProduct(product)} style={{ padding: "4px 8px" }} />
										<Button className="p-button-text" label="Issue" onClick={() => this.openIssue(product.id)} style={{ padding: "4px 8px" }} />
									<Button className="p-button-text" label="Windows" onClick={() => this.setState({ windowsForProductId: this.state.windowsForProductId === product.id ? null : product.id })} style={{ padding: "4px 8px" }} />
										<Button className="p-button-text p-button-danger" icon={<FontAwesomeIcon icon={faTrash} />} onClick={() => this.removeProduct(product)} style={{ padding: "4px 8px" }} />
									</div>
								</div>
							))}
						</div>

						{this.state.windowsForProductId &&
							<SaleWindows
								onError={(message: string) => this.toast.show({ closable: false, detail: message, life: 5000, severity: "error", summary: "Sale window" })}
								parentId={this.state.windowsForProductId}
								parentType={SaleWindowParent.SeasonTicketProduct}
								showValidity
							/>}

						<div style={{ alignItems: "center", display: "flex", gap: 12, justifyContent: "space-between", margin: "30px 0 12px" }}>
							<h3 style={{ margin: 0 }}>Holders</h3>
							<div style={{ alignItems: "center", display: "flex", gap: 8 }}>
								<select
									className="tk-select"
									onChange={(e) => {
										const value = e.target.value ? Number(e.target.value) : null;
										this.setState({ holdersProductId: value });
										this.loadHolders(value ?? undefined);
									}}
									value={holdersProductId ?? ""}
								>
									<option value="">All season tickets</option>
									{seasonProducts.map((p) => <option key={`f-${p.id}`} value={p.id}>{p.name}</option>)}
								</select>
								<Button className="p-button-outlined" disabled={holders.length === 0} icon="pi pi-download" label="Export CSV" onClick={() => this.exportHolders()} />
							</div>
						</div>

						{holders.length === 0 && <span style={{ color: "var(--pb-text-muted)" }}>No season tickets issued yet.</span>}

						{holders.length > 0 && <div className="tk-holders">
							<div className="tk-holder tk-holder-head">
								<span>Holder</span><span>Season ticket</span><span>Valid</span><span>Status</span><span />
							</div>
							{holders.map((holder) => (
								<div className="tk-holder" key={`h-${holder.id}`}>
									<span>
										{holder.holderName || <em style={{ color: "var(--pb-text-muted)" }}>Unnamed</em>}
										{holder.holderEmail && <small>{holder.holderEmail}</small>}
									</span>
									<span>{holder.productName}</span>
									<span>{seasonLabel(holder.validFrom, holder.validTo)}</span>
									<span className={`tk-status tk-status-${holder.status}`}>{STATUS_LABELS[holder.status] || holder.status}</span>
									<span className="tk-holder-actions">
										{holder.status === 0 && <Button className="p-button-text" label="Suspend" onClick={() => this.setHolderStatus(holder, 10)} style={{ padding: "2px 6px" }} />}
										{holder.status === 10 && <Button className="p-button-text" label="Reactivate" onClick={() => this.setHolderStatus(holder, 0)} style={{ padding: "2px 6px" }} />}
										{holder.status !== 20 && <Button className="p-button-text p-button-danger" label="Void" onClick={() => this.setHolderStatus(holder, 20)} style={{ padding: "2px 6px" }} />}
									</span>
								</div>
							))}
						</div>}
					</>}
				</>}

				{activeTab === "competitions" && <>
					<div style={{ alignItems: "center", display: "flex", justifyContent: "space-between", marginBottom: 18 }}>
						<span style={{ color: "var(--pb-text-muted)" }}>The league and cups you play in. Set one on each fixture, and season tickets can cover it.</span>
						<Button icon="pi pi-plus" label="Add competition" onClick={() => this.openCompetition()} />
					</div>

					{competitions.length === 0 &&
						<div className="tk-empty">
							<FontAwesomeIcon icon={faTrophy} />
							<h3>No competitions yet</h3>
							<p>Add your league and any cups. A fixture with no competition is covered by no season ticket, so it&rsquo;s worth setting them on every fixture.</p>
						</div>}

					<div className="tk-grid">
						{competitions.map((competition) => (
							<div className="tk-tile" key={`cp-${competition.id}`}>
								<div className="tk-tile-head"><FontAwesomeIcon icon={faTrophy} /></div>
								<h4>{competition.name}</h4>
								{competition.shortName && <span className="tk-meta">{competition.shortName}</span>}
								<div className="tk-tile-actions">
									<Button className="p-button-text" label="Edit" onClick={() => this.openCompetition(competition)} style={{ padding: "4px 8px" }} />
									<Button className="p-button-text p-button-danger" icon={<FontAwesomeIcon icon={faTrash} />} onClick={() => this.removeCompetition(competition)} style={{ padding: "4px 8px" }} />
								</div>
							</div>
						))}
					</div>
				</>}

				<Dialog
					header={this.fixtureDialogTitle()}
					modal
					onHide={() => this.setState({ showFixtureDialog: false })}
					style={{ maxWidth: "96vw", width: 1080 }}
					visible={this.state.showFixtureDialog}
				>
					{this.renderFixtureDetail()}
				</Dialog>

				<CreateFixture
					fixture={this.state.editFixture}
					onClose={() => this.setState({ showCreateFixture: false })}
					onError={(message) => this.toast.show({ closable: false, detail: message, life: 5000, severity: "error", summary: "Fixture" })}
					onSaved={(fixtureId) => {
						const wasEdit = !!this.state.editFixture;
						this.setState({ showCreateFixture: false, editFixture: null });
						this.toast.show({
							closable: false,
							detail: wasEdit ? "Changes saved." : "Now set its capacity and prices.",
							life: 4000, severity: "success", summary: "Fixture saved"
						});
						// Reload the list, then reopen whatever was just worked on.
						const reopen = fixtureId || this.state.selectedFixtureId;
						this.load().then(() => { if (reopen) this.selectFixture(reopen); });
					}}
					visible={this.state.showCreateFixture}
				/>

				<Dialog footer={<>
					<Button className="p-button-text" label="Cancel" onClick={() => this.setState({ showProductDialog: false })} />
					<Button disabled={this.state.saving} icon="pi pi-check" label={this.state.saving ? "Saving…" : "Save"} onClick={() => this.saveProduct()} />
				</>} header={this.state.editProduct?.id ? "Edit season ticket" : "New season ticket"} modal onHide={() => this.setState({ showProductDialog: false })} style={{ width: 520 }} visible={this.state.showProductDialog}>
					{this.state.editProduct && <>
						<label style={{ display: "block", fontWeight: 600, marginBottom: 6 }}>Name</label>
						<InputText autoFocus onChange={(e) => this.setState({ editProduct: { ...this.state.editProduct, name: e.target.value } })} placeholder="Adult Season Ticket 2026/27" style={{ marginBottom: 16, width: "100%" }} value={this.state.editProduct.name} />

						<label style={{ display: "block", fontWeight: 600, marginBottom: 6 }}>Price</label>
						<InputNumber currency="GBP" locale="en-GB" mode="currency" onValueChange={(e) => this.setState({ editProduct: { ...this.state.editProduct, pricePounds: e.value } })} style={{ marginBottom: 16, width: "100%" }} value={this.state.editProduct.pricePounds} />

						<div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
							<div style={{ flex: 1 }}>
								<label style={{ display: "block", fontWeight: 600, marginBottom: 6 }}>Season starts</label>
								<Calendar dateFormat="dd/mm/yy" onChange={(e) => this.setState({ editProduct: { ...this.state.editProduct, seasonStart: e.value } })} style={{ width: "100%" }} value={this.state.editProduct.seasonStart} />
							</div>
							<div style={{ flex: 1 }}>
								<label style={{ display: "block", fontWeight: 600, marginBottom: 6 }}>Season ends</label>
								<Calendar dateFormat="dd/mm/yy" onChange={(e) => this.setState({ editProduct: { ...this.state.editProduct, seasonEnd: e.value } })} style={{ width: "100%" }} value={this.state.editProduct.seasonEnd} />
							</div>
						</div>

						<label style={{ display: "block", fontWeight: 600, marginBottom: 6 }}>Covers these competitions</label>
						<MultiSelect
							display="chip"
							onChange={(e) => this.setState({ editProduct: { ...this.state.editProduct, competitionIds: e.value } })}
							optionLabel="name"
							optionValue="id"
							options={competitions}
							placeholder="Pick the competitions"
							style={{ marginBottom: 6, width: "100%" }}
							value={this.state.editProduct.competitionIds}
						/>
						<p style={{ color: "var(--pb-text-muted)", fontSize: 12, margin: "0 0 16px" }}>
							A season ticket only admits someone to fixtures in these competitions. Pick nothing and it covers nothing.
						</p>

						<label style={{ display: "block", fontWeight: 600, marginBottom: 6 }}>How many to sell (optional)</label>
						<InputNumber onValueChange={(e) => this.setState({ editProduct: { ...this.state.editProduct, capacity: e.value } })} placeholder="Unlimited" style={{ marginBottom: 16, width: "100%" }} value={this.state.editProduct.capacity} />

						<div style={{ alignItems: "center", display: "flex", gap: 12, marginBottom: 10 }}>
							<InputSwitch checked={!!this.state.editProduct.onSale} onChange={(e) => this.setState({ editProduct: { ...this.state.editProduct, onSale: e.value } })} />
							<label style={{ fontWeight: 600 }}>On sale</label>
							<span style={{ color: "var(--pb-text-muted)", fontSize: 12 }}>Turning this off doesn&rsquo;t affect passes already issued.</span>
						</div>

						<div style={{ alignItems: "center", display: "flex", gap: 12 }}>
							<InputSwitch checked={!!this.state.editProduct.isConcession} onChange={(e) => this.setState({ editProduct: { ...this.state.editProduct, isConcession: e.value } })} />
							<label style={{ fontWeight: 600 }}>Concession</label>
						</div>
					</>}
				</Dialog>

				<Dialog footer={<>
					<Button className="p-button-text" label="Cancel" onClick={() => this.setState({ showIssueDialog: false })} />
					<Button disabled={this.state.saving} icon="pi pi-check" label={this.state.saving ? "Issuing…" : "Issue"} onClick={() => this.issue()} />
				</>} header="Issue a season ticket" modal onHide={() => this.setState({ showIssueDialog: false })} style={{ width: 460 }} visible={this.state.showIssueDialog}>
					{this.state.issueForm && <>
						<p style={{ color: "var(--pb-text-muted)", marginTop: 0 }}>
							For directors&rsquo; passes, comps, and season tickets sold over the counter.
						</p>

						<label style={{ display: "block", fontWeight: 600, marginBottom: 6 }}>Season ticket</label>
						<select
							className="tk-select"
							onChange={(e) => this.setState({ issueForm: { ...this.state.issueForm, seasonTicketProductId: Number(e.target.value) } })}
							style={{ marginBottom: 16, width: "100%" }}
							value={this.state.issueForm.seasonTicketProductId ?? ""}
						>
							{seasonProducts.map((p) => <option key={`i-${p.id}`} value={p.id}>{p.name}</option>)}
						</select>

						<label style={{ display: "block", fontWeight: 600, marginBottom: 6 }}>Holder name</label>
						<InputText autoFocus onChange={(e) => this.setState({ issueForm: { ...this.state.issueForm, holderName: e.target.value } })} placeholder="A. Supporter" style={{ marginBottom: 16, width: "100%" }} value={this.state.issueForm.holderName} />

						<label style={{ display: "block", fontWeight: 600, marginBottom: 6 }}>Email (optional)</label>
						<InputText onChange={(e) => this.setState({ issueForm: { ...this.state.issueForm, holderEmail: e.target.value } })} placeholder="So you can reach them" style={{ width: "100%" }} value={this.state.issueForm.holderEmail} />
					</>}
				</Dialog>

				<Dialog footer={<>
					<Button className="p-button-text" label="Cancel" onClick={() => this.setState({ showCompetitionDialog: false })} />
					<Button disabled={this.state.saving} icon="pi pi-check" label={this.state.saving ? "Saving…" : "Save"} onClick={() => this.saveCompetition()} />
				</>} header={this.state.editCompetition?.id ? "Edit competition" : "New competition"} modal onHide={() => this.setState({ showCompetitionDialog: false })} style={{ width: 440 }} visible={this.state.showCompetitionDialog}>
					{this.state.editCompetition && <>
						<label style={{ display: "block", fontWeight: 600, marginBottom: 6 }}>Name</label>
						<InputText autoFocus onChange={(e) => this.setState({ editCompetition: { ...this.state.editCompetition, name: e.target.value } })} placeholder="Northern Premier League Division One East" style={{ marginBottom: 16, width: "100%" }} value={this.state.editCompetition.name} />

						<label style={{ display: "block", fontWeight: 600, marginBottom: 6 }}>Short name (optional)</label>
						<InputText onChange={(e) => this.setState({ editCompetition: { ...this.state.editCompetition, shortName: e.target.value } })} placeholder="NPL D1E" style={{ width: "100%" }} value={this.state.editCompetition.shortName} />
					</>}
				</Dialog>

				<Dialog footer={<>
					<Button className="p-button-text" label="Cancel" onClick={() => this.setState({ showTypeDialog: false })} />
					<Button disabled={this.state.saving} icon="pi pi-check" label={this.state.saving ? "Saving…" : "Save"} onClick={() => this.saveType()} />
				</>} header={this.state.editType?.id ? "Edit ticket type" : "New ticket type"} modal onHide={() => this.setState({ showTypeDialog: false })} style={{ width: 440 }} visible={this.state.showTypeDialog}>
					{this.state.editType && <>
						<label style={{ display: "block", fontWeight: 600, marginBottom: 6 }}>Name</label>
						<InputText autoFocus onChange={(e) => this.setState({ editType: { ...this.state.editType, name: e.target.value } })} placeholder="Adult" style={{ marginBottom: 16, width: "100%" }} value={this.state.editType.name} />

						<label style={{ display: "block", fontWeight: 600, marginBottom: 6 }}>Price</label>
						<InputNumber currency="GBP" locale="en-GB" mode="currency" onValueChange={(e) => this.setState({ editType: { ...this.state.editType, pricePounds: e.value } })} style={{ marginBottom: 16, width: "100%" }} value={this.state.editType.pricePounds} />

						<label style={{ display: "block", fontWeight: 600, marginBottom: 6 }}>Capacity (optional)</label>
						<InputNumber onValueChange={(e) => this.setState({ editType: { ...this.state.editType, capacity: e.value } })} placeholder="Uses the fixture capacity" style={{ marginBottom: 16, width: "100%" }} value={this.state.editType.capacity} />

						<div style={{ alignItems: "center", display: "flex", gap: 12 }}>
							<InputSwitch checked={!!this.state.editType.isConcession} onChange={(e) => this.setState({ editType: { ...this.state.editType, isConcession: e.value } })} />
							<label style={{ fontWeight: 600 }}>Concession</label>
						</div>
					</>}
				</Dialog>
			</div>
		);
	}
}
